import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { arcLatLngs } from "@/components/map/FlowLayer";
import { addBasemap } from "@/lib/basemap";

const STAGE_COLOR = ["#3b82f6", "#f59e0b", "#f59e0b", "#10b981"];
const RAINVIEWER_META = "https://api.rainviewer.com/public/weather-maps.json";

const progressFor = (data) => {
  if (data.stage >= 3) return 1;
  const start = Date.parse(data.pickup_date || data.booked_at || "");
  const end = Date.parse(data.estimated_delivery || "");
  if (isFinite(start) && isFinite(end) && end > start) {
    return Math.min(0.94, Math.max(0.06, (Date.now() - start) / (end - start)));
  }
  return 0.35;
};

const wxLine = (r) => {
  if (!r) return "";
  const t = r.temp_hi != null ? ` · ${Math.round(r.temp_hi)}°F` : "";
  return `<br/><span style="opacity:.75">${r.label}${t}</span>`;
};

export default function TrackMap({ data }) {
  const el = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!el.current || !data?.route) return;
    const { olat, olng, dlat, dlng } = data.route;
    const color = STAGE_COLOR[data.stage] || STAGE_COLOR[0];
    const map = L.map(el.current, { zoomControl: false, scrollWheelZoom: false });
    addBasemap(map, { dark: true });

    const pts = arcLatLngs({ lat: olat, lng: olng }, { lat: dlat, lng: dlng });
    L.polyline(pts, { color, weight: 7, opacity: 0.15 }).addTo(map);
    L.polyline(pts, { color, weight: 2.5, opacity: 0.9 }).addTo(map);

    const originIcon = L.divIcon({
      className: "",
      html: `<div style="width:12px;height:12px;border-radius:9999px;background:#10b981;border:2px solid #fff;box-shadow:0 0 8px #10b98199"></div>`,
      iconSize: [12, 12], iconAnchor: [6, 6],
    });
    const destIcon = L.divIcon({
      className: "",
      html: `<div style="width:16px;height:16px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>`,
      iconSize: [16, 16], iconAnchor: [8, 16],
    });
    const place = (p) => [p?.city, p?.state].filter(Boolean).join(", ") || p?.zip || "";
    L.marker([olat, olng], { icon: originIcon }).addTo(map)
      .bindTooltip(`<b>${place(data.origin)}</b>${wxLine(data.weather?.origin)}`,
        { permanent: true, direction: "top", offset: [0, -8], className: "trackmap-tip" });
    L.marker([dlat, dlng], { icon: destIcon }).addTo(map)
      .bindTooltip(`<b>${place(data.destination)}</b>${wxLine(data.weather?.destination)}`,
        { permanent: true, direction: "bottom", offset: [0, 6], className: "trackmap-tip" });

    if (data.stage === 1 || data.stage === 2) {
      const prog = progressFor(data);
      const ll = pts[Math.min(Math.round(prog * (pts.length - 1)), pts.length - 1)];
      const truck = L.divIcon({
        className: "",
        html: `<div style="width:28px;height:28px;border-radius:9999px;background:#0b0f14;border:2px solid ${color};box-shadow:0 0 12px ${color}99;display:flex;align-items:center;justify-content:center">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
        </div>`,
        iconSize: [28, 28], iconAnchor: [14, 14],
      });
      L.marker(ll, { icon: truck, zIndexOffset: 500 }).addTo(map)
        .bindTooltip(`Your freight — ~${Math.round(prog * 100)}% of transit window`, { direction: "top", offset: [0, -16] });
    }

    map.fitBounds([[olat, olng], [dlat, dlng]], { padding: [56, 56], maxZoom: 7 });

    fetch(RAINVIEWER_META).then((r) => r.json()).then((j) => {
      const frames = (j?.radar?.past || []).slice(-5);
      if (!frames.length) return;
      const layers = frames.map((f) =>
        L.tileLayer(`${j.host}${f.path}/256/{z}/{x}/{y}/2/1_1.png`, { opacity: 0, zIndex: 350 }).addTo(map));
      let i = layers.length - 1;
      layers[i].setOpacity(0.5);
      timerRef.current = setInterval(() => {
        layers[i].setOpacity(0);
        i = (i + 1) % layers.length;
        layers[i].setOpacity(0.5);
      }, 900);
    }).catch(() => {});

    const t0 = setTimeout(() => map.invalidateSize(), 150);
    return () => { clearTimeout(t0); clearInterval(timerRef.current); map.remove(); };
  }, [data]);

  if (!data?.route) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-white/[0.08]" data-testid="track-map">
      <style>{`.trackmap-tip{background:rgba(13,18,25,.92)!important;border:1px solid rgba(255,255,255,.14)!important;color:#e4e4e7!important;font-size:11px;border-radius:6px;padding:3px 8px;box-shadow:0 2px 8px rgba(0,0,0,.5)!important}.trackmap-tip::before{display:none}`}</style>
      <div ref={el} className="h-64 w-full bg-[#0d1219]" />
      <div className="flex items-center justify-between border-t border-white/[0.06] bg-[#0d1219] px-3 py-1.5 text-[10px] text-zinc-500">
        <span>Live lane view · animated weather radar</span>
        <span className="flex items-center gap-1"><span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: STAGE_COLOR[data.stage] || STAGE_COLOR[0] }} />{["Booked", "In transit", "Out for delivery", "Delivered"][data.stage] || ""}</span>
      </div>
    </div>
  );
}
